import { reactive, watch, computed, toRefs, ref } from "vue";
import { getData, getDataAsync, getDataWithoutLimit } from "helpers/_api";
import { config, env, apiHostname } from "config";
import { checkLogin, destroyTokens, getApiId, initApiId } from "services/JwtService.js";

import { cache, initCache, clearCache } from "helpers/_cache";

import { isCommunity } from "config/router";

import { useRoute } from "vue-router";

import { getUsergroupFilters } from "store/filters";
import { companyStore } from "store/company";
import { department } from "store/department";

const cacheableValues = {
	companies: null,
	company: null,
	contact: null,
	user_id: null
};

const portalDefaults = {
	loading: false,
	unauthorized: false,
	fetching: [],
	me: {
		data: {
			id: null,
			extra: {
				usergroup_ids: []
			}
		}
	},
	...cacheableValues,

	user: [],
	workflowboard: [],
	workflowphase: [],
	workflowcard: [],
	workflowcardtype: [],
	workflowproperty: []
};

export const portalStore = reactive({
	...portalDefaults
});
Object.keys(cacheableValues).forEach((key) => {
	initCache("portal", key, portalStore);
});

// Our data
export const isCompanyUndefinedAllowed = computed(() => {
	const tmpAllowed = env("company_undefined_allowed");
	if (tmpAllowed) return true;
	const tmpConfig = env("company_undefined_allowed_by_usergroupids");
	if (!tmpConfig) return false;
	return tmpConfig?.some(a => usergroup_ids.value.map(b => `${b}`).includes(a));
});

export const me = computed(() => portalStore.me);
export const user_id = computed(() => portalStore.me.data.id);
export const usergroup_ids = computed(() => portalStore.me?.extra?.usergroup_ids ?? []);
export const unauthorized = computed(() => {
	if (portalStore.unauthorized || (portalStore.contact === undefined)) {
		return true;
	}
	if ((portalStore.company === undefined) && !isCompanyUndefinedAllowed.value) {
		return true;
	}
	return false;
});

export const contact = computed(() => portalStore.contact?.[0] ?? cache('portal', 'contact')?.[0]);
export const companies = computed(async () => {
	return portalStore.company ?? [];
});
export const company_ids = computed(() => {
	if (!portalStore.company?.length) return [];
	return portalStore.company.map(a => `${a.data.id}`);
});

export const rolePropertyNames = computed(() => env("variables.contact.properties.role"));
export const role = computed(() => {
	const tmpValue = value(contact.value, env("variables.contact.properties.role"));
	if (tmpValue) {
		return tmpValue.split(",");
	}
	return [];
});
export const highest_role = computed(() => {
	if (!role.value.length) return "";
	const roleHierarchy = env("role_hierarchy") ?? [];
	const highestRole = roleHierarchy.find(a => role.value.includes(a));
	return highestRole ?? role.value[0];
});
export const highest_role_by_values = (values = []) => {
	return computed(() => {
		const roleHierarchy = env("role_hierarchy") ?? [];
		const highestRole = roleHierarchy.find(a => values.includes(a));
		if (highestRole) return highestRole;
		return values?.[0] ?? "";
	}).value;
};

export const higher_roles = computed(() => {
	const roleHierarchy = env("role_hierarchy") ?? [];
	if (!role.value.length) return roleHierarchy;
	const highestRole = roleHierarchy.find(a => role.value.includes(a));
	const highestRoleIndex = roleHierarchy.indexOf(highestRole);
	return roleHierarchy.slice(0, highestRoleIndex);
});
export const lower_roles = computed(() => {
	if (!role.value.length) return [];
	const roleHierarchy = env("role_hierarchy") ?? [];
	const highestRole = roleHierarchy.find(a => role.value.includes(a));
	const highestRoleIndex = roleHierarchy.indexOf(highestRole);
	return roleHierarchy.slice(highestRoleIndex);
});

export const role_disabled = (data) => {
	return computed(() => {
		if (!contact.value) return true;
		if (`${contact.value.data.id}` === `${data.id}`) return true;

		const targetRole = highest_role_by_values(value(data, rolePropertyNames.value) ?? []);
		if (higher_roles.value.includes(targetRole)) {
			return true;
		}
		return false;
	}).value;
};

const user_fallback = "https://i0.wp.com/agency.ibizz.nl/public/themes/admin/_compiled/images/avatar.jpg?ssl=1";
export const user_avatar = (id = user_id.value) => {
	return computed(() => {
		if (`${id}` === `${user_id.value}`) {
			return portalStore.me?.data?.avatar_url ?? user_fallback;
		}
		return portalStore.user.find(a => `${a.data.id}` === `${id}`)?.data?.avatar_url ?? user_fallback;
	}).value;
};

const map = {
	"contact_id" : computed(() => contact.value?.id ?? null)
};
export const mapValue = (key) => {
	return map[key] ?? undefined;
};

// export const values = (keys) => {
// 	return computed(() => {
// 		const result = {};
// 		keys.forEach(key => {
// 			result[key] = portalStore.json.data?.[key] ?? "";
// 		});
// 		return result;
// 	}).value;
// };

export const resolveValueParts = (key, parent_key, index = 0) => {
	if (department.value && (parent_key in department.value)) {
		return department.value[parent_key].data?.[key];
	} else if (key === "id") {
		return portalStore[parent_key]?.[index]?.data?.[key];
	} else if (parent_key) {
		return value(portalStore[parent_key]?.[0], key, "name", true);
	} else if (key === "user_id") {
		return user_id.value ?? null;
	}
	return undefined;

};

const resolveField = (data, target_value, target_key = "name", as_array = false) => {
	return computed(() => {
		const field = data?.extra?.workflowproperties?.find(a => a.field[target_key] === target_value);
		if (!field) return undefined;

		if (field.value && field.field.type === 'CheckboxField'){
			const tmpValue = typeof field.value === "object" ? Object.values(field.value) : field.value;
			if (!as_array && Array.isArray(tmpValue)) {
				field.value = tmpValue.join(', ');
			}
		}
		if (as_array && !Array.isArray(field.value)) {
			field.value = field.value ? [field.value] : [];
		}
		return field;
	}).value;
};
const resolveValue = (data, target_value, target_key = "name", as_array = false) => {
	return computed(() => {
		const field = resolveField(data, target_value, target_key, as_array);
		return field ? field.value : undefined;
	}).value;
};

export const field = (data, target_value, target_key = "name") => {
	return computed(() => {
		return resolveField(data, target_value , target_key);
	}).value;
};
export const value = (data, target_values, target_key = "name", as_array = false) => {
	return computed(() => {
		if (!target_values) return "";
		if (typeof target_values === "string") {
			target_values = [target_values];
		}

		const result = [];
		target_values.forEach(key => {
			if (as_array) {
				result.push(...(resolveValue(data, key, target_key, as_array) ?? []));
			} else {
				result.push(resolveValue(data, key, target_key) ?? "");
			}
		});
		return as_array ? result.filter(a => a !== "") : result.filter(a => a !== "").join(" ");
	}).value;
};

export const fetchRelated = (key = "contact") => {
	if (!user_id.value) {
		return;
	}

	const filters = JSON.parse(JSON.stringify(env(`variables.${key}.filters`)));
	let failed_resolving = false;
	filters.forEach(filter => {
		const target = filter[1];
		// Make sure we target the correct key
		let tmpValue = JSON.parse(JSON.stringify(target.linked_ids ?? ""));
		if (!tmpValue) {
			tmpValue = JSON.parse(JSON.stringify(target.v ?? target.value));
		}
		// Make sure it is a array to work with
		if (!Array.isArray(tmpValue)) {
			tmpValue = [tmpValue];
		}
		// Loop through our values
		tmpValue.map(a => `${a}`).forEach((value, index) => {
			// console.log("value", value);
			// console.log("index", index);
			if (value.startsWith("%") && value.endsWith("%")) {
				// Replace % with our value
				const tmpValue = value.replace(/%/g, "").replace(/%/g, "");
				const tmpParts = tmpValue.split(".");
				const tmpKey = tmpParts[tmpParts.length - 1];
				const tmpParent = tmpParts.length > 1 ? tmpParts[0] : null;
				// console.log("tmpValue",tmpValue);
				// console.log("tmpParts",tmpParts);
				// console.log("tmpKey",tmpKey);
				// console.log("tmpParent",tmpParent);
				if (tmpParent && !portalStore.fetching.includes(tmpParent)) {
					portalStore.fetching.push(tmpParent);
					// Listen for changes
					fetchRelated(tmpParent);
					watch(
						() => portalStore[tmpParent],
						(parent_object) => {
							if (!parent_object) return;
							// Restart our current function based on new input
							fetchRelated(key);
						}, {
							immediate: true
						}
					);
				}

				const newValue = resolveValueParts(tmpKey, tmpParent);
				if (!newValue || (Array.isArray(newValue) && !newValue.length)) {
					failed_resolving = true;
					return;
				}
				if (target.linked_ids) {
					target.linked_ids[index] = newValue;
				} else {
					if (!(value in target)) {
						target.value = [];
					}
					if (Array.isArray(newValue)) {
						// Make sure the %..% variable is removed
						delete target.value[index];
						// Now add the values
						target.value.push(...newValue);
					} else {
						// Replace the value in case of string
						target.value[index] = newValue;
					}
					delete target.v;
				}
			}
		});
	});
	if (!failed_resolving) {
		portalStore.fetching.push(key);
		// console.log("filters", key, filters);
		getDataAsync(`${apiHostname}/api/v1.0/workflowcard`, [
			...filters,
			[
				"FieldFilter",
				{
					"c": "workflowcardtype_id",
					"v": env(`variables.${key}.workflowcardtype_id`)
				}
			],
			[
				"FieldFilter",
				{
					"column": "state",
					"value": "Online"
				}
			]
		]).then((json) => {

			if (key === "company") {
				// Check filters for filter with tmpPropertyName
				const tmpTargetIds = filters.find(a => {
					if (a[0] === "FieldFilter" && a[1].c === "id") {
						return true;
					}
					return false;
				})?.[1]?.value ?? null;

				if (tmpTargetIds) {
					// Reorder json.data based on tmpTargetIds
					json.data = json.data.sort((a, b) => {
						const aIndex = tmpTargetIds.indexOf(`${a.data.id}`);
						const bIndex = tmpTargetIds.indexOf(`${b.data.id}`);
						if (aIndex === -1 && bIndex === -1) return 0; // Both not found, keep order
						if (aIndex === -1) return 1; // a not found, b comes first
						if (bIndex === -1) return -1; // b not found, a comes first
						return aIndex - bIndex; // Sort based on index
					});
				}
			}

			portalStore[key] = json.data?.length ? json.data : undefined;
			if ((key === "contact") && !json.data?.length) {
				portalStore.unauthorized = true;
			}
		});
	} else {
		setTimeout(() => {
			if (portalStore[key]) return;
			portalStore[key] = undefined;
		}, 2000);
	}
	return;
};

export const fetchMe = (renew = false) => {
	if (portalStore.loading || (me.value.data.id !== null && !renew)) return;
	if (!checkLogin()) return;
	portalStore.loading = true;
	// Store current user_id
	if (window.apiUser && (window.apiUser?.user_id !== getApiId())) {
		destroyTokens();
		clearCache();
		portalStore.contact = null;
		portalStore.company = null;
		portalStore.user_id = null;
		initApiId();
	}
	// Set empty object so it can't be queried multiple times
	getData(`${apiHostname}/api/v1.0/me`).then((json) => {
		portalStore.me = json;
		fetchRelated(!!env(`variables.company`) ? "company" : "contact");
		portalStore.user_id = json.data.id;
		portalStore.loading = false;
		companyStore.loading = false;
		// console.log("me", portalStore.me);
	}).catch(error => {
		clearCache();
		resetMe();
		portalStore.contact = undefined;
		portalStore.company = undefined;
		portalStore.user_id = undefined;
		portalStore.unauthorized = true;

		window.location.href = "/";
	});
};
const resetMe = () => {
	portalStore.me = {...portalDefaults.me};
	portalStore.user_id = null;
	portalStore.loading = false;
};



export const department_value = (value) => {
	return computed(() => {
		const tmpValue = value.replace(/%department./g, "").replace(/%/g, "");
		const tmpParts = tmpValue.split(".");
		const tmpKey = tmpParts[tmpParts.length - 1];
		const tmpParent = tmpParts.length > 1 ? tmpParts[0] : null;
		// console.log("tmpValue", tmpValue);
		// console.log("tmpParts", tmpParts);
		// console.log("tmpKey", tmpKey);
		// console.log("tmpParent", tmpParent);
		const tmpTarget = department.value?.[tmpParent];
		return tmpTarget?.data?.[tmpKey] ?? null;
	}).value;
};

export const related_value = (value) => {
	return computed(() => {
		// Replace % with our value
		const tmpValue = value.replace(/%/g, "").replace(/%/g, "");
		const tmpParts = tmpValue.split(".");
		const tmpKey = tmpParts[tmpParts.length - 1];
		const tmpParent = tmpParts.length > 1 ? tmpParts[0] : null;
		// console.log("tmpValue",tmpValue);
		// console.log("tmpParts",tmpParts);
		// console.log("tmpKey",tmpKey);
		// console.log("tmpParent",tmpParent);
		if (tmpParent && !portalStore.fetching.includes(tmpParent)) {
			portalStore.fetching.push(tmpParent);
			// Listen for changes
			fetchRelated(tmpParent);
		}
		return resolveValueParts(tmpKey, tmpParent);
	}).value;
};

export const item = (module_access, id) => {
	return computed(() => {
		if (!(module_access in portalStore)) {
			portalStore[module_access] = [];
		}
		return portalStore[module_access].find(a => (`${a.data.id}` === `${id}`));
	}).value;
};
export const fetchItem = (module_access, id) => {
	return new Promise(resolve => {
		if (typeof id === "undefined" || id === "undefined") return resolve(undefined);
		if (!(module_access in portalStore)) {
			portalStore[module_access] = [];
		}
		const defined = portalStore[module_access].find(a => `${a.data.id}` === `${id}`);
		if (defined) {
			return resolve(defined);
		};
		portalStore[module_access].push({data: {id: id}});
		getDataAsync(`${apiHostname}/api/v1.0/${module_access}/${id}`).then((json) => {
			const index = portalStore[module_access].findIndex(a => `${a.data.id}` === `${id}`);
			portalStore[module_access][index] = json;
			resolve(portalStore[module_access].find(a => `${a.data.id}` === `${id}`));
		});
	});
};


// Fetch functions
const getItems = (module_access, id) => {
	return new Promise((resolve, reject) => {
		let apiUrl = config.value[module_access].url;
		// If ID exists, pass it to the url
		id && (apiUrl = `${apiUrl}/${id}`);
		const stateFilters = [
			[
				"FieldFilter",
				{
					"column": "state",
					"value": "Online"
				}
			]
		];
		const tmpUsers = {
			data: []
		};
		getDataWithoutLimit(`${apiHostname}${apiUrl}`, stateFilters, tmpUsers, "data").then(() => {
			const tmpData = tmpUsers.data.map(a => {
				return {data: a};
			});
			resolve(tmpData);
		});
	});
};

let userWatcher;
export const init = () => {
	// Keep track of the route to handle changes
	const route = useRoute();

	// Check for login state, if logged in make sure me is fetched
	watch(
	    () => route.path,
	    (path) => {
	        if (!checkLogin() || path === "/login") {
				return resetMe();
			}
			fetchMe();
	    },
	    {
	        immediate: true
	    }
	);
	// Based on our user_id and configuration set within public/env/...json
	// Get our company and contact information
	const pastUserId = ref(null);


	watch(
		() => [
			user_id.value,
			env(`variables.company.filters`),
			env(`variables.contact.filters`)
		],
		(values) => {
			// const hasCompany = !!env(`variables.company`);
			if (values.some(a => typeof a === "undefined")) return;
			// Skip when already loading
			if (pastUserId.value === user_id.value) return;
			// Get our company
			fetchRelated("company");
			// Store our user Id for reference.
			pastUserId.value = user_id.value;
		},
		{
			immediate: true
		}
	);
	watch(
		() => portalStore.company,
		(val) => {
			// If company is undefined and allowed get all companies
			if ((val === undefined) && isCompanyUndefinedAllowed.value) {
				const tmpData = {
					data: []
				};
				// Get company by filters, making sure
				const tmpFilters = JSON.parse(JSON.stringify(env(`variables.company.filters`))).filter(a => {
					if (Array.isArray(a[1].v)) {
						// Check if any of the values contains % ... %
						if (a[1].v.some(value => value.startsWith("%") && value.endsWith("%"))) {
							return false;
						}
					}
					return true;
				});
				// NOTE: ADDED EXTRA BOOLEAN TO FIX RESOLVING MISMATCH
				// NOTE: ADDED EXTRA BOOLEAN TO FIX RESOLVING MISMATCH
				// NOTE: ADDED EXTRA BOOLEAN TO FIX RESOLVING MISMATCH
				getDataWithoutLimit(`${apiHostname}/api/v1.0/workflowcard`, [
					...tmpFilters,
					[
						"FieldFilter",
						{
							"c": "workflowcardtype_id",
							"v": env(`variables.company.workflowcardtype_id`)
						}
					],
					[
						"FieldFilter",
						{
							"column": "state",
							"value": "Online"
						}
					]
				], tmpData, "data", false).then(() => {
					portalStore.companies = JSON.parse(JSON.stringify(tmpData.data));
				});
			}
		},
		{
			immediate: true,
			deep: true
		}
	);

	// Store our user information
	// @TODO Check if the get for users is needed, since unclear
	// @TODO Check if the get for users is needed, since unclear
	// @TODO Check if the get for users is needed, since unclear
	// @TODO Check if the get for users is needed, since unclear
	// watch(
	// 	() => user_id.value,
	// 	() => {
	// 		if (!user_id.value) return;
	//
	// 		userWatcher = watch(
	// 			() => route.path,
	// 			(path) => {
	// 				if (!checkLogin() || path === "/login") return;
	// 				!portalStore["user"].length && getItemsAndStore("user");
	// 				userWatcher && userWatcher();
	// 			},
	// 			{
	// 				immediate: true
	// 			}
	// 		);
	// 	}
	// );
};


// Added watcher to clear cache when user_id changes
watch(
	() => user_id.value,
	(newId, oldId) => {
		if (!oldId || !newId) return;
		// Clear our cache since user_id changed
		clearCache();
	}
);

watch(
	() => usergroup_ids.value,
	(ids) => {
		if (!ids || !ids.length) return;

		getUsergroupFilters();
	},
	{
		immediate: true
	}
);